/* The standard component and reference menus, over a mark, a region or a reference.
 *
 * Built like the flexible block diagram's, and for the same reason: `getCommonMenuData` is where
 * every entry a component or a reference offers lives, so a view that assembles its own would be a
 * second, diverging copy of the product's menu. What differs is only how the target is identified -
 * the diagram hands over graph items, and this view hands over what its hit-tests resolved.
 *
 * One target at a time, because this view has no selection model. The diagram's menu acts on a
 * multi-selection and has to reconcile the pointer with it; here a secondary click resolves one
 * mark, one region or one reference, and that is what the menu is about.
 *
 * The model interfaces are read directly, against `no-model-interface-call`, and the disables
 * below are deliberate rather than deferred. A menu's entries depend on what the entity is *when
 * it is opened* - its permissions, its locks, whether it still exists - so a copy resolved
 * upstream and passed down would offer entries for a state that has moved on. `getCommonMenuData`
 * and every menu under it read the same interfaces for the same reason, and the flexible block
 * diagram resolves its targets this way too.
 */

import { isComponent, isReference, ViewIds } from '@ardoq/api-types';
import type { ArdoqId } from '@ardoq/api-types';
import { componentInterface } from '@ardoq/component-interface';
import { referenceInterface } from '@ardoq/reference-interface';
import { DropdownOptionType, type DropdownItem } from '@ardoq/dropdown-menu';
import { dispatchAction } from '@ardoq/rxbeach';
import type { I18nLocale } from '@ardoq/i18n';
import type { NetworkContextTarget, NetworkNode } from '@ardoq/xyz-charts';
import {
  type ContextMenuAppState,
  contextMenuRequested,
} from 'contextMenus/contextMenuRequested';
import { getCommonMenuData } from 'contextMenus/getCommonMenuData';
import { getZoomToFitContextMenuItem } from 'contextMenus/utils';
import { buildReferenceComponents } from 'contextMenus/referenceComponentsInterface';
import { getScenarioContextMenuOptionsForView } from 'scope/scenarioMenuOptions';

export type Network3DContextMenuArgs = {
  target: NetworkContextTarget;
  at: { clientX: number; clientY: number };
  /**
   * The component behind a mark, or `null` where the mark is a group the grouping rules invented.
   *
   * A function rather than the map, because that is the question asked: a node id is a *mark* id,
   * and only the builder knows which marks stand for components.
   */
  toComponentId: (nodeId: string) => ArdoqId | null;
  /** The reference behind each drawn edge, parallel to `graph.edges`. */
  referenceIds: readonly (ArdoqId | null)[];
  /** Names this canvas to anything that has to find a node again after the menu closes. */
  viewInstanceId: string;
  i18nLocale: I18nLocale;
  /**
   * Flies the camera into the clicked group, or `null` where there is no region to fly into.
   *
   * Resolved by the mounted canvas and passed in, because whether a group has a region drawn for
   * it is the renderer's answer - `toGroupVolumes` leaves out a group of one and a group with no
   * marks beneath it. A reference, a plain mark and a collapsed group all arrive here as `null`.
   */
  zoomToGroup: VoidFunction | null;
};

/**
 * The component a mark stands for, or `null` where it stands for a group the rules invented.
 *
 * A mark's `matchKey` is its component id wherever it has one - both builder paths set it from an
 * id they verified against the scope data - and the graph-item id otherwise, so membership of
 * `componentIds` is the whole test. Not `matchKey` on its own: a group carries one too, and a
 * graph-item id handed to the component menu is an id it cannot find and reports.
 *
 * Curried, and resolved on demand rather than as a map built per emission, because a secondary
 * click is rare and the presentation is rebuilt on every settings write.
 */
export const toMarkComponentId =
  (nodes: readonly NetworkNode[], componentIds: readonly ArdoqId[]) =>
  (nodeId: string): ArdoqId | null => {
    const node = nodes.find(({ id }) => id === nodeId);
    if (node === undefined) return null;
    return componentIds.includes(node.matchKey) ? node.matchKey : null;
  };

/**
 * Which component or which reference a target names, and never both.
 *
 * The whole of the decision, and pure, so it can be tested without standing up the model
 * interfaces. Three of the four answers are `null` on an ordinary right-click: a reference names
 * no component, a group the grouping rules invented has no component behind it, and the grouped
 * path draws edges for references it could not resolve.
 */
export const toContextTargetIds = (
  target: NetworkContextTarget,
  toComponentId: (nodeId: string) => ArdoqId | null,
  referenceIds: readonly (ArdoqId | null)[]
): { componentId: ArdoqId | null; referenceId: ArdoqId | null } =>
  target.kind === 'edge'
    ? { componentId: null, referenceId: referenceIds[target.edgeIndex] ?? null }
    : { componentId: toComponentId(target.nodeId), referenceId: null };

/**
 * The target as the model attributes the menus want.
 *
 * Through the global interfaces rather than the loaded scope data this view derives everything
 * else from, which is the one place it reaches for them. `getCommonMenuData` and every menu under
 * it takes `APIComponentAttributes` and `APIReferenceAttributes`, so a scope-data reader would
 * have to rebuild those - and the flexible block diagram, also viewpoint-only, resolves them the
 * same way.
 */
const toEntities = ({
  target,
  toComponentId,
  referenceIds,
}: Pick<
  Network3DContextMenuArgs,
  'target' | 'toComponentId' | 'referenceIds'
>) => {
  const { componentId, referenceId } = toContextTargetIds(
    target,
    toComponentId,
    referenceIds
  );

  return {
    componentId,
    components: [
      componentId === null
        ? undefined
        : // oxlint-disable-next-line ardoq-lint/no-model-interface-call -- read when the menu opens
          componentInterface.get(componentId),
    ].filter(isComponent),
    references: [
      referenceId === null
        ? undefined
        : // oxlint-disable-next-line ardoq-lint/no-model-interface-call -- read when the menu opens
          referenceInterface.get(referenceId),
    ].filter(isReference),
  };
};

/**
 * The view's own entry and the entity entries, in the order they are shown.
 *
 * Separated from the resolution above because this is the part with a decision in it, and the
 * only part that can be read without the model interfaces standing.
 *
 * `getZoomToFitContextMenuItem` rather than an item of this view's own: the yFiles views and the
 * flexible block diagram all put that entry at the top of a right-clicked item's menu, with the
 * same label, icon and translations, and a second one worded differently would make the same
 * action read as a different one depending on which view the reader was in. Divided from what
 * follows it the way those views divide it - this entry acts on the picture, everything under it
 * acts on the data.
 */
export const toNetwork3DMenuOptions = ({
  entityOptions,
  zoomToGroup,
  i18nLocale,
}: {
  entityOptions: readonly DropdownItem[];
  zoomToGroup: VoidFunction | null;
  i18nLocale: I18nLocale;
}): DropdownItem[] | null => {
  if (zoomToGroup === null) {
    return entityOptions.length > 0 ? [...entityOptions] : null;
  }

  const zoomToGroupItem = getZoomToFitContextMenuItem(zoomToGroup, i18nLocale);

  /* No divider with nothing under it, which is a menu that looks like it lost its entries. A
     region the grouping rules invented reaches this with nothing to divide from. */
  return entityOptions.length === 0
    ? [zoomToGroupItem]
    : [
        zoomToGroupItem,
        { name: 'divider', type: DropdownOptionType.DIVIDER },
        ...entityOptions,
      ];
};

/**
 * The menu for what a secondary click landed on, or `null` where there is nothing to offer.
 *
 * `null` rather than an empty menu, and the caller opens nothing: a menu with no entries reads as
 * a broken one. Still reachable - a reference the grouped path could not resolve names nothing,
 * and a mark that is neither a component nor a group with a region has neither of the two things
 * this menu is built from.
 */
export const toNetwork3DContextMenuItems = (
  {
    target,
    at,
    toComponentId,
    referenceIds,
    viewInstanceId,
    i18nLocale,
    zoomToGroup,
  }: Network3DContextMenuArgs,
  {
    permissionContext,
    featureContext,
    currentContext,
    hasCollapsingRuleApplied,
    workspaceAndModel,
    componentsById,
    referenceCreation,
  }: ContextMenuAppState
): DropdownItem[] | null => {
  const { componentId, components, references } = toEntities({
    target,
    toComponentId,
    referenceIds,
  });

  const { options } = getCommonMenuData(
    {
      /* No element and no event. Both are for menus that measure or position against the DOM
         node that was clicked, and what was clicked here is a point on a canvas - the position
         this view has is the pointer's, which it passes as `x` and `y`. */
      target: null,
      event: null,
      /**
       * The node, since there is no element to name it.
       *
       * `ContextMenuSourceNode` is the shape for a view that draws rather than renders, and Drag
       * to create reference is what reads it: the tether has to follow a node that moves without
       * anything scrolling. Given truthfully here so the entry acts on the mark that was clicked
       * rather than on a component id, which a grouping can draw several times.
       *
       * Anchoring it also wants `canvasHitTestRegistry.register(viewInstanceId, { hitTest,
       * locate })`, which this view does not do yet - `resolveSourceHandle` then yields a handle
       * `fromCanvas` cannot locate, and the gesture leaves the tether unanchored rather than
       * failing. Behind `core-viewpoints_tmp_reference-creation-noodle`.
       */
      sourceNode:
        target.kind === 'node'
          ? { viewInstanceId, nodeId: target.nodeId }
          : undefined,
      x: at.clientX,
      y: at.clientY,
      isViewpointMode: true,
    },
    {
      components,
      referenceComponents: buildReferenceComponents(references),
      /* The id the component menu treats as the one right-clicked. Withheld for a reference, and
         for a group with no component behind it, since the menu reports any id it cannot find
         among the components resolved. */
      eventTargetModelId: componentId ?? undefined,
      workspaceAndModel,
      componentsById,
      referenceCreation,
    },
    i18nLocale,
    permissionContext,
    getScenarioContextMenuOptionsForView({
      isViewpointMode: true,
      viewId: ViewIds.NETWORK_3D,
      featureContext,
      permissionContext,
      context: currentContext,
      hasCollapsingRuleApplied,
      i18nLocale,
    })
  );

  return toNetwork3DMenuOptions({
    entityOptions: options ?? [],
    zoomToGroup,
    i18nLocale,
  });
};

/** Opens the menu through the app's shared context-menu request, which supplies the app state and positions and dismisses it. */
export const openNetwork3DContextMenu = (
  args: Network3DContextMenuArgs
): void =>
  dispatchAction(
    contextMenuRequested({
      position: { left: args.at.clientX, top: args.at.clientY },
      getItems: appState => toNetwork3DContextMenuItems(args, appState) ?? [],
    })
  );
